import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Layouts and route guards load eagerly (needed for routing logic)
import Layout from './components/layout/Layout';
import ClientLayout from './components/layout/ClientLayout';
import DistributorLayout from './components/layout/DistributorLayout';
import OrgLayout from './components/layout/OrgLayout';
import PrivateRoute from './routes/PrivateRoute';
import AdminRoute from './routes/AdminRoute';
import ClientAdminRoute from './routes/ClientAdminRoute';
import ClinicAdminRoute from './routes/ClinicAdminRoute';
import DistributorRoute, { DistributorWriteRoute } from './routes/DistributorRoute';
import OrgRoute from './routes/OrgRoute';
import { ClinicsProvider } from './contexts/ClinicsContext';
import { UserProvider } from './contexts/UserContext';
import { ThemeProvider } from './context/ThemeContext';
import { useAppVersion } from './hooks/useAppVersion';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes default
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// All page components lazy-loaded for code splitting
const Login = React.lazy(() => import('./components/auth/Login'));
const AcceptInvite = React.lazy(() => import('./components/auth/AcceptInvite'));
const AttestationGuidedPage = React.lazy(() => import('./components/auth/AttestationGuidedPage'));
const MarketingShotsFrame = React.lazy(() => import('./pages/MarketingShotsFrame'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const PatientsPage = React.lazy(() => import('./pages/PatientsPage'));
const EditPatientPage = React.lazy(() => import('./pages/EditPatientPage'));
const ChartNoteUploadPage = React.lazy(() => import('./pages/ChartNoteUploadPage'));
const ProductsPage = React.lazy(() => import('./pages/ProductsPage'));
const DocumentCombinerPage = React.lazy(() => import('./pages/DocumentCombinerPage'));
const WoundAssessment = React.lazy(() => import('./components/assessments/WoundAssessment'));
const TreatmentPlan = React.lazy(() => import('./components/treatment/TreatmentPlan'));
const Supplies = React.lazy(() => import('./components/supplies/Supplies'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));

// Distributor Pages
const DistributorDashboardPage = React.lazy(() => import('./pages/DistributorDashboardPage'));
// DistributorCompaniesPage is lazy-loaded inside DistributorManagementPage
const DistributorCompanyDetailPage = React.lazy(() => import('./pages/DistributorCompanyDetailPage'));
// DistributorUsersPage is lazy-loaded inside DistributorManagementPage
const DistributorClinicsPage = React.lazy(() => import('./pages/DistributorClinicsPage'));
const DistributorProductsPage = React.lazy(() => import('./pages/DistributorProductsPage'));
const DistributorAnalyticsPage = React.lazy(() => import('./pages/DistributorAnalyticsPage'));
const DistributorReportsPage = React.lazy(() => import('./pages/DistributorReportsPage'));
const DistributorPatientsPage = React.lazy(() => import('./pages/DistributorPatientsPage'));
const DistributorSubmissionsPage = React.lazy(() => import('./pages/DistributorSubmissionsPage'));
const DistributorPatientDetailPage = React.lazy(() => import('./pages/DistributorPatientDetailPage'));
const DistributorPatientEditPage = React.lazy(() => import('./pages/DistributorPatientEditPage'));

// Organization Pages
const OrgDashboardPage = React.lazy(() => import('./pages/OrgDashboardPage'));
// OrgManagementGroupsPage, OrgAffiliatesPage, OrgUsersPage
// are lazy-loaded inside OrgManagementPage
const OrgClinicsPage = React.lazy(() => import('./pages/OrgClinicsPage'));
const OrgDistributorsPage = React.lazy(() => import('./pages/OrgDistributorsPage'));
const OrgProductsPage = React.lazy(() => import('./pages/OrgProductsPage'));
const OrgAnalyticsPage = React.lazy(() => import('./pages/OrgAnalyticsPage'));
const OrgReportsPage = React.lazy(() => import('./pages/OrgReportsPage'));
const OrgPatientsPage = React.lazy(() => import('./pages/OrgPatientsPage'));
const OrgSubmissionsPage = React.lazy(() => import('./pages/OrgSubmissionsPage'));

// Admin/Internal Pages
const AddPatientPage = React.lazy(() => import('./pages/AddPatientPage'));
const AnalyticsPage = React.lazy(() => import('./pages/AnalyticsPage'));
const EditCompanyPage = React.lazy(() => import('./pages/EditCompanyPage'));
const ClinicsPage = React.lazy(() => import('./pages/ClinicsPage'));
const ClinicDetailPage = React.lazy(() => import('./pages/ClinicDetailPage'));
const AddClinicPage = React.lazy(() => import('./pages/AddClinicPage'));
const EditClinicPage = React.lazy(() => import('./pages/EditClinicPage'));
const PhysiciansPage = React.lazy(() => import('./pages/PhysiciansPage'));
const CRMPage = React.lazy(() => import('./pages/CRMPage'));
const ClientDetailPage = React.lazy(() => import('./pages/ClientDetailPage'));
const AdminReportsPage = React.lazy(() => import('./pages/AdminReportsPage'));
const AdminBillingPage = React.lazy(() => import('./pages/AdminBillingPage'));
const AdminAttestationPage = React.lazy(() => import('./pages/AdminAttestationPage'));
const AdminOrganizationsPage = React.lazy(() => import('./pages/AdminOrganizationsPage'));
const AdminManagementPage = React.lazy(() => import('./pages/AdminManagementPage'));
const UserDetailPage = React.lazy(() => import('./pages/UserDetailPage'));
const EntityDetailPage = React.lazy(() => import('./pages/EntityDetailPage'));
const EmployeeActivityPage = React.lazy(() => import('./pages/EmployeeActivityPage'));
const AdminDocumentsPage = React.lazy(() => import('./pages/AdminDocumentsPage'));
const AdminClinicDetailPage = React.lazy(() => import('./pages/AdminClinicDetailPage'));
const AdminEmailPage = React.lazy(() => import('./pages/AdminEmailPage'));

// Client Pages
const ClientChartSubmissionPage = React.lazy(() => import('./pages/ClientChartSubmissionPage'));
const ClientPatientChartSubmissionPage = React.lazy(() => import('./pages/ClientPatientChartSubmissionPage'));
const ClientIVRSubmissionPage = React.lazy(() => import('./pages/ClientIVRSubmissionPage'));
const ClientPatientEditPage = React.lazy(() => import('./pages/ClientPatientEditPage'));
const ClientClinicsPage = React.lazy(() => import('./pages/ClientClinicsPage'));
const ClientClinicDetailPage = React.lazy(() => import('./pages/ClientClinicDetailPage'));
const OrgClinicDetailPage = React.lazy(() => import('./pages/OrgClinicDetailPage'));
const DistributorClinicDetailPage = React.lazy(() => import('./pages/DistributorClinicDetailPage'));
const ClientProvidersPage = React.lazy(() => import('./pages/ClientProvidersPage'));
const ClientAdminManagementPage = React.lazy(() => import('./pages/ClientAdminManagementPage'));
const ClinicAdminManagementPage = React.lazy(() => import('./pages/ClinicAdminManagementPage'));
const ClientAdminReportsPage = React.lazy(() => import('./pages/ClientAdminReportsPage'));
const UnifiedSubmissionsPage = React.lazy(() => import('./pages/UnifiedSubmissionsPage'));
const SubmissionDetailPage = React.lazy(() => import('./pages/SubmissionDetailPage'));
const AdminSubmissionDetailPage = React.lazy(() => import('./pages/AdminSubmissionDetailPage'));
const DenialInfoPage = React.lazy(() => import('./pages/DenialInfoPage'));
const ChartNoteProcessingPage = React.lazy(() => import('./pages/ChartNoteProcessingPage'));
const ClientUnifiedSubmissionsPage = React.lazy(() => import('./pages/ClientUnifiedSubmissionsPage'));
const InsuranceVerificationDemoPage = React.lazy(() => import('./pages/InsuranceVerificationDemoPage'));
const ClientResourceManagerPage = React.lazy(() => import('./pages/ClientResourceManagerPage'));
const AdminOrderingPage = React.lazy(() => import('./pages/AdminOrderingPage'));
const ClientPatientsPage = React.lazy(() => import('./pages/ClientPatientsPage'));
const ClientPatientDetailPage = React.lazy(() => import('./pages/ClientPatientDetailPage'));

// Rep Pages
const RepUnifiedSubmissionsPage = React.lazy(() => import('./pages/RepUnifiedSubmissionsPage'));
const RepOrderingPage = React.lazy(() => import('./pages/RepOrderingPage'));

// Resource Pages
const OrgResourceManagerPage = React.lazy(() => import('./pages/OrgResourceManagerPage'));
const CombinedResourcesPage = React.lazy(() => import('./pages/CombinedResourcesPage'));
const AffiliateProductsPage = React.lazy(() => import('./pages/AffiliateProductsPage'));

// Management Pages (org & distributor)
const OrgManagementPage = React.lazy(() => import('./pages/OrgManagementPage'));
const DistributorManagementPage = React.lazy(() => import('./pages/DistributorManagementPage'));

const PatientAuditLog = React.lazy(() => import('./pages/PatientAuditLog'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const SupportPage = React.lazy(() => import('./pages/SupportPage'));

// Loading fallback
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress />
  </Box>
);

function App() {
  useAppVersion();

  return (
    <QueryClientProvider client={queryClient}>
    <UserProvider>
    <ThemeProvider>
      <CssBaseline />
      <ClinicsProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/attest" element={<AttestationGuidedPage />} />
            <Route path="/preview/landing-shots/patients-frame" element={<MarketingShotsFrame />} />

            {/* Protected Routes */}
            <Route element={<PrivateRoute />}>
              {/* Organization Layout Routes */}
              <Route element={<OrgLayout />}>
                <Route element={<OrgRoute />}>
                  <Route path="org/dashboard" element={<OrgDashboardPage />} />
                  <Route path="org/patients" element={<OrgPatientsPage />} />
                  <Route path="org/patients/:patientId" element={<ClientPatientDetailPage />} />
                  <Route path="org/patients/:patientId/edit-details" element={<ClientPatientEditPage />} />
                  <Route path="org/patients/:patientId/submit-ivr" element={<ClientIVRSubmissionPage />} />
                  <Route path="org/submissions" element={<OrgSubmissionsPage />} />
                  <Route path="org/management" element={<OrgManagementPage />} />
                  <Route path="org/clinics-view" element={<OrgClinicsPage />} />
                  <Route path="org/distributors-view" element={<OrgDistributorsPage />} />
                  <Route path="org/clinics/:clinicId" element={<OrgClinicDetailPage />} />
                  {/* Legacy routes redirect to management tabs */}
                  <Route path="org/management-groups" element={<Navigate to="/org/management?tab=management-groups" replace />} />
                  <Route path="org/affiliates" element={<Navigate to="/org/management?tab=affiliates" replace />} />
                  <Route path="org/clinics" element={<Navigate to="/org/management?tab=clinics" replace />} />
                  <Route path="org/users" element={<Navigate to="/org/management?tab=users" replace />} />
                  <Route path="org/products" element={<OrgProductsPage />} />
                  <Route path="org/resources" element={<OrgResourceManagerPage />} />
                  <Route path="org/reports" element={<OrgReportsPage />} />
                  <Route path="org/analytics" element={<OrgAnalyticsPage />} />
                  <Route path="org/settings" element={<SettingsPage />} />
                </Route>
              </Route>

              {/* Distributor Layout Routes */}
              <Route element={<DistributorLayout />}>
                <Route element={<DistributorRoute />}>
                  <Route path="distributor/dashboard" element={<DistributorDashboardPage />} />
                  <Route path="distributor/patients" element={<DistributorPatientsPage />} />
                  <Route path="distributor/patients/new" element={<DistributorPatientEditPage />} />
                  <Route path="distributor/patients/:patientId" element={<DistributorPatientDetailPage />} />
                  <Route path="distributor/patients/:patientId/edit-details" element={<DistributorPatientEditPage />} />
                  <Route path="distributor/patients/:patientId/submit-ivr" element={<ClientIVRSubmissionPage />} />
                  <Route path="distributor/submissions" element={<DistributorSubmissionsPage />} />
                  <Route element={<DistributorWriteRoute />}>
                    <Route path="distributor/management" element={<DistributorManagementPage />} />
                    <Route path="distributor/companies" element={<Navigate to="/distributor/management?tab=affiliates" replace />} />
                    <Route path="distributor/clinics" element={<Navigate to="/distributor/management?tab=clinics" replace />} />
                    <Route path="distributor/users" element={<Navigate to="/distributor/management?tab=users" replace />} />
                    <Route path="distributor/reports" element={<DistributorReportsPage />} />
                    <Route path="distributor/analytics" element={<DistributorAnalyticsPage />} />
                  </Route>
                  <Route path="distributor/clinics-view" element={<DistributorClinicsPage />} />
                  <Route path="distributor/clinics/:clinicId" element={<DistributorClinicDetailPage />} />
                  <Route path="distributor/companies/:companyId" element={<DistributorCompanyDetailPage />} />
                  <Route path="distributor/products" element={<DistributorProductsPage />} />
                  <Route path="distributor/resources" element={<CombinedResourcesPage />} />
                  <Route path="distributor/settings" element={<SettingsPage />} />
                </Route>
              </Route>

              {/* Client Layout Routes */}
              <Route element={<ClientLayout />}>
                <Route path="client/patients" element={<ClientPatientsPage />} />
                <Route path="client/patients/new" element={<ClientPatientEditPage />} />
                <Route path="client/patients/:patientId" element={<ClientPatientDetailPage />} />
                <Route path="client/clinics" element={<ClientClinicsPage />} />
                <Route path="client/clinics/:clinicId" element={<ClientClinicDetailPage />} />
                <Route path="client/providers" element={<ClientProvidersPage />} />
                <Route path="client/crm" element={<ClientUnifiedSubmissionsPage />} />
                <Route path="client/resources" element={<CombinedResourcesPage />} />
                <Route path="client/products" element={<ProductsPage />} />
                <Route path="client/settings" element={<SettingsPage />} />
                <Route path="client/patients/:patientId/submit-ivr" element={<ClientIVRSubmissionPage />} />
                <Route path="client/patients/:patientId/edit-details" element={<ClientPatientEditPage />} />

                {/* Client Admin Routes */}
                <Route element={<ClientAdminRoute />}>
                  {/* New tabbed management surface mirrors AdminManagementPage. */}
                  <Route path="client-admin/management" element={<ClientAdminManagementPage />} />
                  {/* Legacy /client-admin/users still works — points at the user-onboarding tab. */}
                  <Route path="client-admin/users" element={<Navigate to="/client-admin/management?tab=users" replace />} />
                  <Route path="client-admin/reports" element={<ClientAdminReportsPage />} />
                </Route>

                {/* Clinic Admin Routes */}
                <Route element={<ClinicAdminRoute />}>
                  <Route path="clinic-admin/management" element={<ClinicAdminManagementPage />} />
                  <Route path="clinic-admin/users" element={<Navigate to="/clinic-admin/management?tab=users" replace />} />
                  <Route path="clinic-admin/facility" element={<EditClinicPage />} />
                </Route>
              </Route>

              <Route element={<Layout />}>
                {/* Legacy Client routes */}
                <Route path="submit-chart" element={<ClientChartSubmissionPage />} />
                <Route path="patients/:patientId/submit-chart" element={<ClientPatientChartSubmissionPage />} />
                <Route path="patients/:patientId/submit-ivr" element={<ClientIVRSubmissionPage />} />
                <Route path="patients/:patientId/edit-details" element={<ClientPatientEditPage />} />

                {/* Rep routes */}
                <Route path="rep/crm" element={<RepUnifiedSubmissionsPage />} />
                <Route path="rep/resources" element={<CombinedResourcesPage />} />
                <Route path="rep/orders" element={<RepOrderingPage />} />
                <Route path="rep/products" element={<AffiliateProductsPage />} />

                {/* Internal staff routes */}
                <Route element={<AdminRoute />}>
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="patients" element={<PatientsPage />} />
                  <Route path="patients/new" element={<AddPatientPage />} />
                  <Route path="patients/:patientId" element={<ClientPatientDetailPage />} />
                  <Route path="patients/:id/edit" element={<EditPatientPage />} />
                  <Route path="patients/:id/upload" element={<ChartNoteUploadPage />} />
                  <Route path="patients/:patientId/submit-ivr" element={<ClientIVRSubmissionPage />} />
                  <Route path="patients/:patientId/edit-details" element={<ClientPatientEditPage />} />
                  <Route path="patients/:patientId/audit" element={<PatientAuditLog />} />
                  <Route path="assessments" element={<WoundAssessment />} />
                  <Route path="treatment" element={<TreatmentPlan />} />
                  <Route path="supplies" element={<Supplies />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="support" element={<SupportPage />} />
                  <Route path="clinics" element={<ClinicsPage />} />
                  <Route path="clinics/new" element={<AddClinicPage />} />
                  <Route path="clinics/:clinicId" element={<ClinicDetailPage />} />
                  <Route path="clinics/:clinicId/edit" element={<EditClinicPage />} />
                  <Route path="physicians" element={<PhysiciansPage />} />
                  <Route path="products" element={<ProductsPage />} />
                  <Route path="tools/combine-pdf" element={<DocumentCombinerPage />} />
                  <Route path="insurance-verification-demo" element={<InsuranceVerificationDemoPage />} />

                  {/* Admin-specific routes */}
                  <Route path="admin/crm" element={<CRMPage />} />
                  <Route path="admin/crm/client/:clientId" element={<ClientDetailPage />} />
                  <Route path="admin/reports" element={<AdminReportsPage />} />
                  <Route path="admin/billing" element={<AdminBillingPage />} />
                  <Route path="admin/attestations" element={<AdminAttestationPage />} />
                  <Route path="admin/unified-submissions" element={<UnifiedSubmissionsPage />} />
                  <Route path="admin/unified-submissions/:submissionId" element={<AdminSubmissionDetailPage />} />
                  <Route path="admin/unified-submissions/:submissionId/legacy" element={<SubmissionDetailPage />} />
                  <Route path="admin/unified-submissions/:submissionId/denial-info" element={<DenialInfoPage />} />
                  <Route path="admin/unified-submissions/:submissionId/process-chart" element={<ChartNoteProcessingPage />} />
                  <Route path="admin/orders" element={<AdminOrderingPage />} />
                  <Route path="admin/management" element={<AdminManagementPage />} />
                  {/* Legacy /admin/onboarding redirects into the new Partner Onboarding tab. */}
                  <Route path="admin/onboarding" element={<Navigate to="/admin/management?tab=partner-onboarding" replace />} />
                  {/* Unified management detail routes */}
                  <Route path="management/users/:id" element={<UserDetailPage />} />
                  <Route path="management/:type/:id" element={<EntityDetailPage />} />
                  <Route path="admin/system-diagnostics" element={<EmployeeActivityPage />} />
                  {/* Legacy routes redirect to unified management page */}
                  <Route path="admin/users" element={<Navigate to="/admin/management?tab=users" replace />} />
                  <Route path="admin/companies" element={<Navigate to="/admin/management?tab=affiliates" replace />} />
                  {/* Manufacturers admin (Organizations = manufacturers in the remodel).
                      The remodel had redirected this into Partner Onboarding, which only
                      ASSIGNS existing manufacturers and can't CREATE one — leaving no way
                      to add a manufacturer. Restore the full org/manufacturer CRUD page. */}
                  <Route path="admin/organizations" element={<AdminOrganizationsPage />} />
                  <Route path="admin/distributors" element={<Navigate to="/admin/management?tab=management-groups" replace />} />
                  <Route path="admin/analytics" element={<AnalyticsPage />} />
                  <Route path="admin/client-resources" element={<ClientResourceManagerPage />} />
                  <Route path="admin/documents" element={<AdminDocumentsPage />} />
                  <Route path="admin/emails" element={<AdminEmailPage />} />
                  <Route path="companies/:companyId/edit" element={<EditCompanyPage />} />
                  <Route path="companies/:companyId/clinics/:clinicId" element={<AdminClinicDetailPage />} />
                </Route>
              </Route>
            </Route>

            {/* Catch all */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ClinicsProvider>
    </ThemeProvider>
    </UserProvider>
    </QueryClientProvider>
  );
}

export default App;
